import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { normalizeTheme, type ThemeMode } from '../theme';

export type UiState = {
  /** Last-used model — preselected when starting a new conversation. */
  lastModelId: string | null;
  /** Last-used dataset group slugs — preselected when starting a new conversation. */
  lastEnabledGroups: string[];
  /** Last-used skill slug — preselected when starting a new conversation. */
  lastSkillSlug: string | null;
  /** Appearance preference shared by the web UI. */
  theme: ThemeMode;
  favoriteSkillKeys: string[];
  toggleFavoriteSkill: (key: string) => void;
  /** Text size preference shared by the web UI. */
  textSize: 'extraSmall' | 'small' | 'medium' | 'large';
  /** Phones and tablets: read the answer aloud after a question spoken with push to talk. */
  readAloudAfterVoice: boolean;
  setReadAloudAfterVoice: (on: boolean) => void;
  /** Read a long answer as a short summary instead of in full; short answers are always read in full. */
  readAloudSummary: boolean;
  setReadAloudSummary: (on: boolean) => void;
  /** Desktop: width of the history sidebar in pixels, dragged by the user; null = default. */
  sidebarWidth: number | null;
  setSidebarWidth: (width: number | null) => void;
  setLastModel: (modelId: string) => void;
  setLastEnabledGroups: (groups: string[]) => void;
  setLastSkill: (slug: string | null) => void;
  setTheme: (theme: UiState['theme']) => void;
  setTextSize: (textSize: UiState['textSize']) => void;
  reset: () => void;
};

export const useUi = create<UiState>()(
  persist(
    (set) => ({
      lastModelId: null,
      lastEnabledGroups: [],
      lastSkillSlug: null,
      favoriteSkillKeys: [],
      toggleFavoriteSkill: (key) =>
        set((state) => ({
          favoriteSkillKeys: state.favoriteSkillKeys.includes(key)
            ? state.favoriteSkillKeys.filter((item) => item !== key)
            : [...state.favoriteSkillKeys, key],
        })),
      theme: 'system',
      textSize: 'medium',
      readAloudAfterVoice: true,
      setReadAloudAfterVoice: (on) => set({ readAloudAfterVoice: on }),
      readAloudSummary: true,
      setReadAloudSummary: (on) => set({ readAloudSummary: on }),
      sidebarWidth: null,
      setSidebarWidth: (width) =>
        set({ sidebarWidth: width === null ? null : clampSidebarWidth(width) }),
      setLastModel: (modelId) => set({ lastModelId: modelId }),
      setLastEnabledGroups: (groups) => set({ lastEnabledGroups: groups }),
      setLastSkill: (slug) => set({ lastSkillSlug: slug }),
      setTheme: (theme) => set({ theme }),
      setTextSize: (textSize) => set({ textSize }),
      reset: () => set({ lastModelId: null, lastEnabledGroups: [], lastSkillSlug: null }),
    }),
    {
      name: 'nc-ui',
      version: 3,
      merge: (persisted, current) => {
        const saved =
          persisted && typeof persisted === 'object' ? (persisted as Partial<UiState>) : {};
        return {
          ...current,
          ...saved,
          favoriteSkillKeys: normalizeFavoriteSkillKeys(saved.favoriteSkillKeys),
          sidebarWidth:
            typeof saved.sidebarWidth === 'number' ? clampSidebarWidth(saved.sidebarWidth) : null,
        };
      },
      migrate: (persisted, version) => {
        if (!persisted || typeof persisted !== 'object') return persisted;
        const state = persisted as Partial<UiState> & { theme?: unknown };
        // Before version 3 the app was light only, so a stored theme was never a choice.
        return { ...state, theme: version < 3 ? 'system' : normalizeTheme(state.theme) };
      },
    },
  ),
);

export function normalizeFavoriteSkillKeys(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(value.filter((key): key is string => typeof key === 'string' && key.length > 0)),
  ];
}

/** The history sidebar stays between these widths (pixels); 312 is the default. */
export const SIDEBAR_WIDTH = { min: 220, default: 312, max: 520 } as const;

export function clampSidebarWidth(width: number): number {
  if (!Number.isFinite(width)) return SIDEBAR_WIDTH.default;
  return Math.round(Math.min(SIDEBAR_WIDTH.max, Math.max(SIDEBAR_WIDTH.min, width)));
}
